import React, { useEffect, useRef, useState } from "react";
import { fetchApi, getUploadsUrl } from "../../config";

const QUOTES = [
    {
        text: "They shipped fast, communicated the whole way, and delivered exactly what we needed. The custom build turned a two-hour daily task into something we never think about anymore.",
        name: "Brad Wiggins",
        role: "Founder, USA",
    },
    {
        text: "My second time working with Kudosta and it's been fantastic both times. They exceeded expectations, worked at all hours, and consistently over-delivered.",
        name: "Logan Barlett",
        role: "USA",
    },
    {
        text: "The work was high quality and, months later, remains bug-free and stable. Whenever a schedule mattered, the right resources were there to deliver on time.",
        name: "Magali Baumgarden",
        role: "France",
    },
    {
        text: "Excellent — I've used Kudosta twice now. Second to none service, and highly recommended.",
        name: "Max Gee",
        role: "UK",
    },
    {
        text: "Quick, skilled and knowledgeable. They completed exactly what I needed and went above and beyond to get the job done.",
        name: "Cleo",
        role: "Smileheartz",
    },
];

const DELAY = 6000;

export default function Testimonials() {
    const [testimonials, setTestimonials] = useState(QUOTES);
    const [loading, setLoading] = useState(true);
    const testimonialsRef = useRef(testimonials);
    const [index, setIndex] = useState(0);
    const viewportRef = useRef(null);
    const touchStartX = useRef(0);
    const timerRef = useRef(null);

    const go = (n) => {
        const len = (testimonialsRef.current && testimonialsRef.current.length) || QUOTES.length;
        setIndex(((n % len) + len) % len);
    };
    const next = () => go(index + 1);
    const prev = () => go(index - 1);

    const stop = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };
    const start = () => {
        const reduce = typeof window.matchMedia === "function"
            ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
            : false;
        if (!reduce && timerRef.current === null) {
            timerRef.current = setInterval(() => {
                const len = (testimonialsRef.current && testimonialsRef.current.length) || QUOTES.length;
                setIndex((i) => (len ? (i + 1) % len : 0));
            }, DELAY);
        }
    };
    const restart = () => {
        stop();
        start();
    };

    useEffect(() => {
        start();
        const onVisibility = () => (document.hidden ? stop() : start());
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            stop();
            document.removeEventListener("visibilitychange", onVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                const res = await fetchApi("get_testimonials.php");
                if (!mounted) return;
                if (res && Array.isArray(res.Testimonials)) {
                    setTestimonials(res.Testimonials);
                    testimonialsRef.current = res.Testimonials;
                    // reset index if out of range
                    setIndex((i) => {
                        const len = res.Testimonials.length || QUOTES.length;
                        return i >= len ? 0 : i;
                    });
                }
            } catch (err) {
                // leave defaults on error
                // console.error(err);
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => { mounted = false; };
    }, []);

    const decodeHtml = (html) => {
        if (!html) return "";
        try {
            const txt = document.createElement("textarea");
            txt.innerHTML = html;
            return txt.value;
        } catch (e) {
            return html;
        }
    };

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        stop();
    };
    const onTouchEnd = (e) => {
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 40) {
            dx < 0 ? next() : prev();
        }
        start();
    };

    return (
        <section className="container kd-quotes">
            <style>{`
                .kd-quotes { text-align: center; padding: 80px 15px; }
                .kd-quotes * { box-sizing: border-box; }
                .kd-slider { position: relative; max-width: 940px; margin: 0 auto; }
                .kd-stage { position: relative; padding: 0 64px; }
                .kd-viewport { overflow: hidden; }
                .kd-track { display: flex; align-items: stretch; transition: transform .5s cubic-bezier(.16,1,.3,1); will-change: transform; gap: 0; }
                .kd-slide { flex: 0 0 100%; width: 100%; min-width: 100%; margin: 0; padding: 6px 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
                .kd-quotes figure, .kd-quotes blockquote, .kd-quotes figcaption { margin-left: 0; margin-right: 0; }
                .kd-mark { line-height: 0; margin: 0 0 16px; }
                .kd-mark svg { width: 40px; height: 40px; display: block; color: var(--orange, #f47a20); }
                .kd-slide blockquote { font-family: var(--font-head, 'Archivo', sans-serif); font-weight: 600; font-size: 25px; line-height: 1.5; color: var(--ink, #15223d); max-width: 720px; margin: 0 0 26px; letter-spacing: -.01em; }
                .kd-who { display: flex; align-items: center; justify-content: center; gap: 14px; }
                .kd-avatar { width: 48px; height: 48px; border-radius: 50%; flex: 0 0 auto; background: linear-gradient(135deg, var(--orange, #f47a20), var(--navy, #15223d)); }
                .kd-avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; object-position: top; }
                .kd-testimonial-skeleton .kd-mark .placeholder-box { width: 32px; height: 28px; min-height: 0; }
                .kd-testimonial-skeleton blockquote { width: 100%; }
                .kd-testimonial-skeleton .placeholder-line { display: block; margin: 8px auto; }
                .kd-testimonial-skeleton .placeholder-line:first-child { width: 92%; }
                .kd-testimonial-skeleton .placeholder-line:nth-child(2) { width: 84%; }
                .kd-testimonial-skeleton .placeholder-line:last-child { width: 55%; }
                .kd-testimonial-skeleton .kd-avatar { background: none; }
                .kd-testimonial-skeleton .kd-avatar .placeholder-box { width: 48px; height: 48px; min-height: 0; border-radius: 50%; }
                .kd-testimonial-skeleton .kd-name .placeholder-line { width: 84px; height: 14px; margin: 0; }
                .kd-testimonial-skeleton .kd-role .placeholder-line { width: 64px; height: 12px; margin: 4px 0 0; }
                .kd-name { font-family: var(--font-head, 'Archivo', sans-serif); font-weight: 700; font-size: 16px; line-height: 1.2; color: var(--ink, #15223d); text-align: left; }
                .kd-role { font-size: 13px; line-height: 1.3; color: var(--muted, #6b7280); text-align: left; margin-top: 2px; }
                .kd-arrow { position: absolute; top: 50%; transform: translateY(-50%); z-index: 3; width: 46px; height: 46px; border-radius: 50%; padding: 0; border: 1px solid var(--line-2, #e6e8ee); background: #fff; color: var(--navy, #15223d); display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 6px 18px rgba(20,33,61,.07); transition: color .15s, border-color .15s, box-shadow .15s, background .15s; }
                .kd-arrow:hover { color: var(--orange, #f47a20); border-color: var(--orange, #f47a20); box-shadow: 0 10px 22px rgba(20,33,61,.13); }
                .kd-arrow svg { width: 20px; height: 20px; display: block; pointer-events: none; }
                .kd-prev { left: 0; }
                .kd-next { right: 0; }
                .kd-dots { display: flex; justify-content: center; gap: 8px; margin-top: 30px; }
                .kd-dots button { width: 8px; height: 8px; padding: 0; border: 0; border-radius: 50%; background: var(--line-2, #d7dbe4); cursor: pointer; transition: background .25s, width .25s; }
                .kd-dots button.is-active { background: var(--orange, #f47a20); width: 24px; border-radius: 5px; }
                @media (max-width: 900px) { .kd-slide blockquote { font-size: 21px; } }
                @media (max-width: 600px) {
                    .kd-quotes { padding: 56px 15px; }
                    .kd-stage { padding: 0; }
                    .kd-arrow { display: none; }
                    .kd-slide blockquote { font-size: 19px; line-height: 1.5; }
                }
            `}</style>

            <div className="kd-slider" onMouseEnter={stop} onMouseLeave={start}>
                <div className="kd-stage">
                    {!loading && (
                        <button aria-label="Previous testimonial" className="kd-arrow kd-prev" type="button" onClick={() => { prev(); restart(); }}>
                            <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"></polyline></svg>
                        </button>
                    )}

                    <div className="kd-viewport" ref={viewportRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} aria-busy={loading}>
                        <div className="kd-track" style={{ transform: `translateX(${-index * 100}%)` }}>
                            {loading ? (
                                <figure className="kd-slide skeleton kd-testimonial-skeleton" role="status" aria-label="Loading testimonials">
                                    <div className="kd-mark"><span className="placeholder-box" /></div>
                                    <blockquote>
                                        <span className="placeholder-line" />
                                        <span className="placeholder-line" />
                                        <span className="placeholder-line" />
                                    </blockquote>
                                    <figcaption className="kd-who">
                                        <span className="kd-avatar"><span className="placeholder-box" /></span>
                                        <div>
                                            <div className="kd-name"><span className="placeholder-line" /></div>
                                            <div className="kd-role"><span className="placeholder-line" /></div>
                                        </div>
                                    </figcaption>
                                </figure>
                            ) : testimonials.map((t) => (
                                <figure className="kd-slide" key={t.id || t.name}>
                                    <div className="kd-mark">
                                        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M9.5 6C6.6 6 5 8.4 5 11.3V18h6.2v-6.7H8.1C8.1 9.7 9 8.6 11 8.3zm9 0c-2.9 0-4.5 2.4-4.5 5.3V18h6.2v-6.7h-3.1c0-1.6.9-2.7 2.9-3z"></path></svg>
                                    </div>
                                    <blockquote>{decodeHtml(t.message || t.text || "")}</blockquote>
                                    <figcaption className="kd-who">
                                        <span className="kd-avatar">
                                            {t.media_url ? (
                                                <img src={getUploadsUrl('testimonials', t.media_url)} alt={t.media_name || t.name} />
                                            ) : null}
                                        </span>
                                        <div>
                                            <div className="kd-name">{t.name}</div>
                                            <div className="kd-role">{t.country || t.role}</div>
                                        </div>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>

                    {!loading && (
                        <button aria-label="Next testimonial" className="kd-arrow kd-next" type="button" onClick={() => { next(); restart(); }}>
                            <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </button>
                    )}
                </div>

                {!loading && <div className="kd-dots" aria-hidden="true">
                    {testimonials.map((q, i) => (
                        <button
                            key={q.id || q.name}
                            type="button"
                            aria-label={`Go to testimonial ${i + 1}`}
                            className={index === i ? "is-active" : ""}
                            onClick={() => { go(i); restart(); }}
                        ></button>
                    ))}
                </div>}
            </div>
        </section>
    );
}
